import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { GPUInstanceType } from "types/InstanceTypes";
import { parseRapidsBaseImage } from "../ContainerSelector/rapidsBaseImages";

export type CreateEnvironmentBuildMode = "featured-container";

export interface CreateEnvironmentUrlState {
  gpu?: string;
  instanceType?: string;
  storage?: string;
  location?: string;
  customRegion?: boolean;
  buildMode?: CreateEnvironmentBuildMode;
  featuredContainer?: string;
  pythonVersion?: string;
  cudaVersion?: string;
  rapidsRelease?: string;
  launchableID?: string;
  jupyter?: boolean;
  networkId?: string;
  networkName?: string;
}

export type CreateEnvironmentUrlUpdates = {
  [K in keyof CreateEnvironmentUrlState]?: CreateEnvironmentUrlState[K] | null;
};

export interface FeaturedContainerUrlParams {
  pythonVersion?: string;
  cudaVersion?: string;
}

const BLOCKED_PARAMS = ["name", "provider"];
const VALID_BUILD_MODES = new Set<CreateEnvironmentBuildMode>([
  "featured-container",
]);

const isNumericStorage = (value: string | null): value is string =>
  !!value && /^[0-9]+$/.test(value) && Number(value) > 0;

const getBooleanParam = (value: string | null): boolean | undefined => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

export const parseCreateEnvironmentSearch = (
  search: string,
): CreateEnvironmentUrlState => {
  const params = new URLSearchParams(search);
  const buildMode = params.get("buildMode");

  return {
    gpu: params.get("gpu") || undefined,
    instanceType: params.get("instanceType") || undefined,
    storage: isNumericStorage(params.get("storage"))
      ? params.get("storage") || undefined
      : undefined,
    location: params.get("location") || undefined,
    customRegion: getBooleanParam(params.get("customRegion")),
    buildMode:
      buildMode &&
      VALID_BUILD_MODES.has(buildMode as CreateEnvironmentBuildMode)
        ? (buildMode as CreateEnvironmentBuildMode)
        : undefined,
    featuredContainer: params.get("featuredContainer") || undefined,
    pythonVersion: params.get("pythonVersion") || undefined,
    cudaVersion: params.get("cudaVersion") || undefined,
    rapidsRelease: params.get("rapidsRelease") || undefined,
    launchableID: params.get("launchableID") || undefined,
    jupyter: getBooleanParam(params.get("jupyter")),
    networkId: params.get("networkId") || undefined,
    networkName: params.get("networkName") || undefined,
  };
};

export const getInitialContainerFromCreateEnvironmentUrl = (
  state: CreateEnvironmentUrlState,
): ContainerSelected => {
  if (state.buildMode === "featured-container") {
    return {
      verbBuild: { containerUrl: state.featuredContainer || "Default" },
    };
  }

  return {
    vmBuild: {
      forceJupyterInstall: state.jupyter ?? true,
    },
  };
};

export const getCreateEnvironmentUrlStateFromContainer = (
  container: ContainerSelected,
  featuredContainerParams: FeaturedContainerUrlParams = {},
): CreateEnvironmentUrlUpdates => {
  if (container.verbBuild) {
    const featuredContainer = container.verbBuild.containerUrl || "Default";
    const rapidsSelection = parseRapidsBaseImage(featuredContainer);
    let pythonVersion: string | undefined;
    let cudaVersion: string | undefined;

    if (rapidsSelection) {
      pythonVersion = rapidsSelection.pythonVersion;
      cudaVersion = rapidsSelection.cudaVersion;
    } else if (featuredContainer === "Default") {
      pythonVersion = featuredContainerParams.pythonVersion;
      cudaVersion = featuredContainerParams.cudaVersion;
    }

    return {
      buildMode: "featured-container",
      featuredContainer,
      pythonVersion,
      cudaVersion,
      rapidsRelease: rapidsSelection?.rapidsVersion,
      launchableID: undefined,
      jupyter: undefined,
    };
  }

  return {
    buildMode: undefined,
    featuredContainer: undefined,
    pythonVersion: undefined,
    cudaVersion: undefined,
    rapidsRelease: undefined,
    launchableID: undefined,
    jupyter: undefined,
  };
};

export const getCreateEnvironmentUrlStateFromLaunchable = (
  launchableID: string,
): CreateEnvironmentUrlUpdates => ({
  buildMode: undefined,
  featuredContainer: undefined,
  pythonVersion: undefined,
  cudaVersion: undefined,
  rapidsRelease: undefined,
  launchableID,
  jupyter: undefined,
});

export const getGpuQueryValueForInstance = (
  instance: GPUInstanceType,
): string => {
  if (!instance.supported_gpus || instance.supported_gpus.length === 0) {
    return "CPU";
  }
  return instance.supported_gpus[0].name;
};

export const getCreateEnvironmentSearch = (
  currentSearch: string,
  updates: CreateEnvironmentUrlUpdates,
): string => {
  const params = new URLSearchParams(currentSearch);

  BLOCKED_PARAMS.forEach((param) => params.delete(param));

  const setOrDelete = (
    key: keyof CreateEnvironmentUrlState,
    value: string | boolean | null | undefined,
  ) => {
    if (value === undefined || value === null || value === "") {
      params.delete(key);
      return;
    }
    params.set(key, String(value));
  };

  if ("gpu" in updates) setOrDelete("gpu", updates.gpu);
  if ("instanceType" in updates) {
    setOrDelete("instanceType", updates.instanceType);
  }
  if ("storage" in updates) {
    setOrDelete(
      "storage",
      isNumericStorage(updates.storage ?? null) ? updates.storage : undefined,
    );
  }
  if ("location" in updates) setOrDelete("location", updates.location);
  if ("customRegion" in updates) {
    setOrDelete("customRegion", updates.customRegion);
  }
  if ("buildMode" in updates) setOrDelete("buildMode", updates.buildMode);
  if ("featuredContainer" in updates) {
    setOrDelete("featuredContainer", updates.featuredContainer);
  }
  if ("pythonVersion" in updates) {
    setOrDelete("pythonVersion", updates.pythonVersion);
  }
  if ("cudaVersion" in updates) {
    setOrDelete("cudaVersion", updates.cudaVersion);
  }
  if ("rapidsRelease" in updates) {
    setOrDelete("rapidsRelease", updates.rapidsRelease);
  }
  if ("launchableID" in updates) {
    setOrDelete("launchableID", updates.launchableID);
  }
  if ("jupyter" in updates) setOrDelete("jupyter", updates.jupyter);
  if ("networkId" in updates) setOrDelete("networkId", updates.networkId);
  if ("networkName" in updates) {
    setOrDelete("networkName", updates.networkName);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
};
